import { useCallback, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { constants, TIMER_RESEND } from 'config/constants'
import { active2fa, create2fa } from 'store/fetchers/auth'
import {
  EMessageTypes,
  getVerifiedToken,
  resendVerificationCode,
  sendVerificationCode,
} from 'store/fetchers/utils'
import { useAppSelector } from 'store/hooks'
import { fetchUser, selectedUser } from 'store/reducers/users'
import { PopupWrapper } from 'components/app/popup'
import PopupCloseBtn from 'components/app/popup/partial/popup-close-btn'
import ButtonWhite from 'components/ui/button/_color/_white'

import { createPortal } from 'react-dom'

import PopupStepper from '../popup/popup-stepper'
import PopupHeading from '../popup/components/__heading'
import PopupSubtitle from '../popup/components/__subtitle'

import { QrCodeMobile, QrCodeText } from './components/qr-code'
import Backup from './components/backup'
import styles from './styles.module.scss'
import { useTranslation } from 'utils/use-translation'

import Rabbit from './assets/rabbit.png'

import CodeVerificationInput from 'components/global/code-verification-input'
import ButtonBlue from 'components/ui/button/_color/_blue'
import { Captcha } from 'components/app/captcha'
import AuthEnable from './components/auth-enable'
import useViewport from 'libs/use-viewport'

export type GoogleAuthPopupProps = {
  onClose: () => void
}

export default function GoogleAuthPopup({ onClose }: GoogleAuthPopupProps) {
  const { t, locale, replacePlaceholders } = useTranslation()
  const view = useViewport()
  const isDesktop = view.includes('desktop')
  const user = useAppSelector(selectedUser)
  const dispatch = useDispatch()

  const verificationTokenRef = useRef('')
  const [step, setStep] = useState(0)

  const [secret2fa, set2FaSecret] = useState('')
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isCodeValid, setIsCodeValid] = useState(false)
  const [errorCode, setErrorCode] = useState<string | null>(null)

  const onCaptchaSuccess = async (captcha: string) => {
    setIsLoading(true)

    const { tokens } = await sendVerificationCode({
      email: user.email,
      phone: user.phone,
      type: EMessageTypes.TFA_ACTIVATION,
      captcha,
    })

    verificationTokenRef.current = tokens[0]

    if (verificationTokenRef.current) setStep(step + 1)

    setIsLoading(false)

    return !!verificationTokenRef.current
  }

  const handleCreate2Fa = async () => {
    setIsLoading(true)

    const { response } = await create2fa()

    if (response?.secret) {
      set2FaSecret(response.secret)
      setStep(step + 1)
    }

    setIsLoading(false)
  }

  const handleResendCode = async (captcha: string) => {
    verificationTokenRef.current = await resendVerificationCode({
      verification_token: verificationTokenRef.current,
      captcha,
    })
  }

  const handleActivate2fa = async (inputCode: string) => {
    const result = await active2fa({
      code_2fa: inputCode,
      secret_2fa: secret2fa,
      verification_token: verificationTokenRef.current,
    })

    if (!result) {
      setErrorCode(t('code_invalid_try_again_later'))
      return
    }

    dispatch(fetchUser())
    setStep(step + 1)
  }

  const verifyCode = useCallback(async (inputCode: string) => {
    setIsLoading(true)
    setErrorCode(null)

    try {
      if (step === 4) {
        handleActivate2fa(inputCode)
      } else {
        const res = await getVerifiedToken({
          code: inputCode,
          verification_token: verificationTokenRef.current,
        })

        if (!res.result) {
          setIsCodeValid(false)
          setErrorCode(res.message || t('something_went_wrong'))
          return
        }

        setIsCodeValid(true)
        verificationTokenRef.current = res.response
        setStep((prev) => prev + 1)
        setCode('')
      }
    } catch (err: any) {
      const msg = err?.response?.message || err?.response?.error || ''

      if (msg.includes('is invalid') || msg.includes('is not valid')) {
        setErrorCode(t('code_is_invalid'))
      } else if (msg.includes('limit exceeded') || msg.includes('try again')) {
        setErrorCode(t('rate_limit_exceeded_retry_later'))
      } else {
        setErrorCode(t('something_went_wrong'))
      }
    } finally {
      setIsLoading(false)
    }
  }, [step, secret2fa, locale])

  const steps = [
    {
      heading: isDesktop ? t('download_auth_app') : t('download_app'),
      subtitle: t('downloan_install_auth_app'),
      content: <>
        <QrCodeMobile className={styles.googleAuth__qr} />
        <div className={styles.lastRow}>
          <div className={styles.step}>
            {replacePlaceholders(t('step_of'), { step: step + 1, length: 5 })}
          </div>
          <div className={styles.buttons}>
            <ButtonWhite className={styles.btnUnset} onClick={onClose}>
              {t('close')}
            </ButtonWhite>
             <ButtonBlue
                onClick={handleCreate2Fa}
                className={styles['button-save-key']}
                isLoading={isLoading}
                disabled={isLoading}>
                {t('next')}
              </ButtonBlue>
          </div>
        </div>
      </>,
      config: {
        removeRow: true,
      },
    },
    {
      heading: t('scan_qr'),
      subtitle: t('add_qr_in_auth_app'),
      content: <QrCodeText value={secret2fa} credentials={user.email || user.phone} className={styles.googleAuth__qr} />,
    },
    {
      heading: t('save_backup_key_in_safe_place'),
      content: (
        <>
          <div className={styles.googleAuth__content}>
            <img src={Rabbit} alt="rabbit" className={styles.rabbit} />
            <Backup value={secret2fa} className={styles.googleAuth__backup} />
          </div>
          <div className={styles.lastRow}>
            <div className={styles.step}>
              {replacePlaceholders(t('step_of'), { step: step + 1, length: 5 })}
            </div>
            <div className={styles.buttons}>
              <ButtonWhite className={styles.btnUnset} onClick={() => setStep(step - 1)}>
                {t('back')}
              </ButtonWhite>
              <Captcha onCaptchaSuccess={onCaptchaSuccess} setIsLoading={setIsLoading}>
                <ButtonBlue
                  className={styles['button-save-key']}
                  isLoading={isLoading}
                  disabled={isLoading}>
                  {t('saved_key_next')}
                </ButtonBlue>
              </Captcha>
            </div>
          </div>
        </>
      ),
      config: {
        removeRow: true,
      },
    },
    {
      heading: t('enable_authenticator'),
      subtitle: t('verify_account_to_add_google_auth'),
      content: (
        <>
          <h2 className={styles.verification}>{t('verification')} <span>1/2</span></h2>
          <p className={styles.verification__text}>
            {replacePlaceholders(t('enter_length_code_sent_to'), { length: constants.codeLength })}: <span>{user?.email ?? `+${user?.phone}`}</span>
          </p>
          <CodeVerificationInput
            code={code}
            setCode={setCode}
            isCodeValid={isCodeValid}
            setIsCodeValid={setIsCodeValid}
            errorMsg={errorCode}
            setErrorMsg={setErrorCode}
            isLoading={isLoading}
            countSeconds={TIMER_RESEND}
            onResendCode={handleResendCode}
            verifyCode={verifyCode}
            resendBtn
            showError
            resendWithCaptcha
          />
          <div className={styles.lastRow}>
            <div className={styles.step}>
              {replacePlaceholders(t('step_of'), { step: step + 1, length: 5 })}
            </div>
            <div className={styles.buttons}>
              <ButtonWhite className={styles.btnUnset} onClick={() => setStep(step - 1)}>
                {t('back')}
              </ButtonWhite>
            </div>
          </div>
        </>
      ),
      config: {
        removeRow: true,
      },
    },
    {
      heading: t('enable_authenticator'),
      subtitle: t('verify_account_to_add_google_auth'),
      content: (
        <>
          <h2 className={styles.verification}>{t('verification')} <span>2/2</span></h2>
          <p className={styles.verification__text} dangerouslySetInnerHTML={{
            __html: t('enter_2fa_to_enable_auth')
          }}></p>
          <CodeVerificationInput
            code={code}
            setCode={setCode}
            isCodeValid={isCodeValid}
            setIsCodeValid={setIsCodeValid}
            errorMsg={errorCode}
            setErrorMsg={setErrorCode}
            isLoading={isLoading}
            countSeconds={60}
            verifyCode={verifyCode}
            showError
          />
          <div className={styles.lastRow}>
            <div className={styles.step}>
              {replacePlaceholders(t('step_of'), { step: step, length: 5 })}
            </div>
            <div className={styles.buttons}>
              <ButtonWhite className={styles.btnUnset} onClick={() => {
                setCode('')
                setErrorCode('')
                setStep(step - 1)
              }}>
                {t('back')}
              </ButtonWhite>
            </div>
          </div>
        </>
      ),
      config: {
        removeRow: true,
      }
    },
    {
      heading: t('authenticator_enabled'),
      content: (
        <>
          <AuthEnable
            position={styles.googleAuth__authEnable}
            text={t('you_enabled_google_auth')}
          />
          <div className={styles.lastRow}>
            <div className={styles.step}>
              {replacePlaceholders(t('step_of'), { step: step, length: 5 })}
            </div>
            <div className={styles.buttons}>
              <ButtonBlue className={styles['btn-done']} onClick={onClose}>
                {t('done')}
              </ButtonBlue>
            </div>
          </div>
        </>
      ),
      config: {
        removeRow: true,
      },
    },
  ]

  return createPortal(
    <PopupWrapper onClose={onClose} classNames={[styles.container]}>
      <PopupStepper
        onClose={onClose}
        step={step}
        setStep={setStep}
        disabled={isLoading}
        pagination={{
          [step]: steps[step]?.config,
        }}
        stepsCount={5}
      >
        {
          steps.map(({ heading, subtitle, content }) => (
            <>
              <PopupHeading
                className={styles.popup__heading}
              >
                {heading}
              </PopupHeading>
              {subtitle && <PopupSubtitle className={styles.googleAuth__subtitle}>{subtitle}</PopupSubtitle>}
              {content}
            </>
          ))
        }
      </PopupStepper>
      <PopupCloseBtn onClose={onClose} className={styles.close__btn} />
    </PopupWrapper>,
    document.body
  )
}
